import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

// Guards
import { AuthcheckerGuard } from './authentication/guard/authchecker.guard';
import { MasterGuardService } from './authentication/guard/masterchecker.guard';

// List of Components
import { SidenavComponent } from './sidenav/sidenav.component';
import { LoginComponent } from './session/login/login.component';
import { ResetpasswordComponent } from './session/resetpassword/resetpassword.component';
import { SetpasswordComponent } from './session/setpassword/setpassword.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { OnboardingFreemiumComponent } from './seo/onboarding-freemium/onboarding-freemium.component';
import { ExperimentViewComponent } from './nudge/nudges/optimization/experiment-view/experiment-view.component';
import { SignupOrganicComponent } from './session/organic/signup/signup.component';
import { SignupPremiumComponent } from './session/organic/signup-premium/signup-premium.component';
import { PhoneNumberComponent } from './session/organic/phone-number/phone-number.component';
import { OrganicLoginComponent } from './session/organic/login/login.component';
import { DownloadServiceWorkerComponent } from './seo/download-service-worker/download-service-worker.component';
import { SharedIssueComponent } from './diagnostics/shared-issue/shared-issue.component';
import { BackendMetricsComponent } from './metrics/backend-metrics/backend-metrics.component';
import { UserPermissionsComponent } from './user-permissions/user-permissions.component';
import { UnderMaintenanceComponent } from './banners/under-maintenance/under-maintenance.component';
import { PaymentConfirmedComponent } from './invoices/payment-confirmed/payment-confirmed.component';
import { HubspotBookingConfirmationComponent } from './hubspot-booking-confirmation/hubspot-booking-confirmation.component';
import { SetupComponent } from './ads/setup/setup.component';
import { BillingFullscreenComponent } from './seo/billing-fullscreen/billing-fullscreen.component';
import { AiChatComponent } from './recordings/ai-chat/ai-chat.component';
import { NudgeAiChatComponent } from './nudge/ai-chat/ai-chat.component';
import { ShareableAuditComponent } from './seo/ai-analysis/shareable-audit/shareable-audit.component';
import { PerformanceValuesComponent } from './nudge/performance/puppeteer/performance-values/performance-values.component';
import { SignalsAiChatComponent } from './signals-insights/ai-chat/ai-chat.component';
import { NotetakingAIChatComponent } from './notetaking/ai-chat/ai-chat.component';
import { ShareableWebsiteAuditComponent } from './website-audit/shareable-component/shareable-component.component';
import { DecodeTokenComponent } from './shared/decode-token/decode-token.component';
import { ShopifyErrorComponent } from './utils/shopify-error/shopify-error.component';

const routes: Routes = [
  {
    path: 'login',
    component: LoginComponent,
  },
  {
    path: 'signup_organic',
    component: SignupOrganicComponent,
  },
  {
    path: 'signup_organic_premium',
    component: SignupPremiumComponent,
  },
  {
    path: 'signup_organic_premium_1',
    component: SignupPremiumComponent,
  },
  {
    path: 'signup_organic_leads',
    component: SignupPremiumComponent,
  },
  {
    path: 'verify-phone',
    component: PhoneNumberComponent,
    canActivate: [AuthcheckerGuard],
  },
  {
    path: 'verify-number',
    component: PhoneNumberComponent,
    canActivate: [AuthcheckerGuard],
  },
  {
    path: 'login_organic',
    component: OrganicLoginComponent,
  },
  {
    path: 'resetpassword',
    component: ResetpasswordComponent,
  },
  {
    path: 'setpassword',
    component: SetpasswordComponent,
  },
  {
    path: 'share/issue',
    component: SharedIssueComponent,
  },
  {
    path: 'download-service-worker',
    component: DownloadServiceWorkerComponent,
  },
  {
    path: 'hubspot-booking-confirmation',
    component: HubspotBookingConfirmationComponent,
  },
  {
    path: 'organic_onboarding',
    component: OnboardingFreemiumComponent,
    canActivate: [MasterGuardService],
    data: {
      privileges: 'seo_projects',
    },
  },
  {
    path: 'ads/setup',
    component: SetupComponent,
    canActivate: [MasterGuardService],
    data: {
      privileges: 'copilot_ads',
    },
  },
  {
    path: 'recording/heatmap',
    canActivate: [MasterGuardService],
    data: {
      privileges: 'recording_heatmap',
    },
    loadChildren: () =>
      import('./recordings/heatmaps/heatmaps.module').then(
        (m) => m.HeatmapsModule,
      ),
  },
  {
    path: 'recording/:heatmapTarget/:id',
    canActivate: [MasterGuardService],
    data: {
      privileges: 'recording_overall',
    },
    loadChildren: () =>
      import('./recordings/player/player.module').then((m) => m.PlayerModule),
  },
  {
    path: 'recording/ai-chat/:chatTarget/:id',
    component: AiChatComponent,
    canActivate: [MasterGuardService],
    data: {
      privileges: 'recordings_ai',
    },
  },
  {
    path: 'nudge/ai-chat/:chatTarget/:id',
    component: NudgeAiChatComponent,
    canActivate: [MasterGuardService],
    data: {
      privileges: 'recordings_ai',
    },
  },
  {
    path: 'signals/ai-chat/:chatTarget/:id',
    component: SignalsAiChatComponent,
    canActivate: [MasterGuardService],
    data: {
      privileges: 'signals_insights',
    },
  },
  {
    path: 'notetaking/ai-chat/:threadId',
    component: NotetakingAIChatComponent,
    canActivate: [MasterGuardService],
    data: {
      privileges: 'notetaking',
    },
  },
  {
    path: 'confirmation/payment',
    component: PaymentConfirmedComponent,
  },
  {
    path: 'organic-billing',
    component: BillingFullscreenComponent,
    canActivate: [MasterGuardService],
    data: {
      privileges: 'seo_billing',
    },
  },
  {
    path: 'share/ai-audit/:token',
    component: ShareableAuditComponent,
  },
  {
    path: 'share/website-audit/:token',
    component: ShareableWebsiteAuditComponent,
  },
  {
    path: 'share/:shortUrl',
    component: DecodeTokenComponent,
  },
  {
    path: 'nudge/puppeteer-performance',
    component: PerformanceValuesComponent,
  },
  {
    path: 'nudge-detailed',
    canActivate: [MasterGuardService],
    data: {
      privileges: 'masterPreview',
    },
    loadComponent: () =>
      import('./nudge-v2/nudge-detailed/nudge-detailed.component').then(
        (m) => m.NudgeDetailedComponent,
      ),
  },
  {
    path: 'shopify-error',
    component: ShopifyErrorComponent,
  },
  {
    path: '',
    component: SidenavComponent,
    canActivate: [AuthcheckerGuard],
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'dashboard',
        component: DashboardComponent,
      },
      {
        path: 'organic-maintenance',
        component: UnderMaintenanceComponent,
      },
      {
        path: 'master-experiment-view',
        component: ExperimentViewComponent,
        canActivate: [MasterGuardService],
        data: {
          privileges: 'masterAIExperiments',
        },
      },
      {
        path: 'metrics/backend',
        component: BackendMetricsComponent,
        canActivate: [MasterGuardService],
        data: {
          privileges: 'backendMetrics',
        },
      },
      {
        path: 'ads',
        loadChildren: () => import('./ads/ads.module').then((m) => m.AdsModule),
      },
      {
        path: 'linkedin',
        loadChildren: () =>
          import('./linkedin/linkedin.module').then((m) => m.LinkedinModule),
      },
      {
        path: 'website-analysis',
        loadChildren: () =>
          import('./websites-analysis/websites-analysis.module').then(
            (m) => m.WebsitesAnalysisModule,
          ),
      },
      {
        path: 'notetaking',
        loadChildren: () =>
          import('./notetaking/notetaking.module').then(
            (m) => m.NotetakingModule,
          ),
      },
      {
        path: 'merchandising',
        loadChildren: () =>
          import('./merchandise/merchandise.module').then(
            (m) => m.MerchandiseModule,
          ),
      },
      {
        path: 'user-management',
        loadChildren: () =>
          import('./metrics/user-management/user-management.module').then(
            (m) => m.UserManagementModule,
          ),
      },
      {
        path: 'master-preview',
        canActivate: [MasterGuardService],
        data: {
          privileges: 'masterPreview',
        },
        loadComponent: () =>
          import('./master-preview/master-preview.component').then(
            (m) => m.MasterPreviewComponent,
          ),
      },
      {
        path: 'model-automation',
        loadChildren: () =>
          import('./model-automation/model-automation.module').then(
            (m) => m.ModelAutomationModule,
          ),
      },
      {
        path: 'invoices',
        loadChildren: () =>
          import('./invoices/invoices.module').then((m) => m.InvoicesModule),
      },
      {
        path: 'diagnostic',
        loadChildren: () =>
          import('./diagnostics/diagnostics.module').then(
            (m) => m.DiagnosticsModule,
          ),
      },
      {
        path: 'seo',
        loadChildren: () => import('./seo/seo.module').then((m) => m.SeoModule),
      },
      {
        path: 'recording',
        data: {
          privileges: 'recording_overall',
        },
        loadChildren: () =>
          import('./recordings/recordings.module').then(
            (m) => m.RecordingsModule,
          ),
      },
      {
        path: 'agents',
        loadChildren: () =>
          import('./agents/automations.module').then(
            (m) => m.AutomationsModule,
          ),
      },
      {
        path: 'user-permissions',
        component: UserPermissionsComponent,
        canActivate: [MasterGuardService],
        data: {
          privileges: 'userPermissions',
        },
      },
      {
        path: 'signal',
        loadChildren: () =>
          import('./signals/signals.module').then((m) => m.SignalsModule),
      },
      {
        path: 'signals/monday/oauth/callback',
        loadChildren: () =>
          import('./signals-insights/signals-insights.module').then(
            (m) => m.SignalsInsightsModule,
          ),
      },
      {
        // insights connected with signals
        path: 'signals',
        loadChildren: () =>
          import('./signals-insights/signals-insights.module').then(
            (m) => m.SignalsInsightsModule,
          ),
      },
      {
        path: 'integrations/klaviyo',
        loadChildren: () =>
          import('./signals-insights/signals-insights.module').then(
            (m) => m.SignalsInsightsModule,
          ),
      },
      {
        path: 'nudgeGPT',
        loadChildren: () =>
          import('./gen-ai-nudges/gen-ai-nudges.module').then(
            (m) => m.GenAiNudgesModule,
          ),
      },
      {
        path: 'website-audit',
        loadChildren: () =>
          import('./website-audit/website-audit.module').then(
            (m) => m.WebsiteAuditModule,
          ),
      },
      {
        path: 'purchase-insights',
        loadChildren: () =>
          import('./purchase-insights/purchase-insights.module').then(
            (m) => m.PurchaseInsightsModule,
          ),
      },
    ],
  },
  { path: '**', redirectTo: '' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
